import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PeopleApiService } from '@core/api/services/people-api.service';
import { CreatePersonRequest, DeletePersonDto, MembershipStatus, OrganizationUnitDto, PersonDto, UpdatePersonRequest } from '@core/api/generated/api-client';
import { mapPersonDtoToPerson } from '@core/api/mappers/person.mapper';
import { Person } from '../models/person.model';
import {
  MinisterialActivityActionDefinition,
  MinisterialActivityEffectiveState,
  MinisterialActivityTransitionPreview,
  MinisterialActivityKind,
  MinistryEventType,
  MinistryStatus,
  PersonLookupOption,
} from '../models/person-event.model';
import {
  CreatePersonCommunityHistoryRequest,
  PersonCommunityHistoryItem,
  UpdatePersonCommunityHistoryRequest,
} from '../models/person-community-history.model';
import { OrganizationUnitItem } from '@features/user/models/filter-data.model';
import { environment } from '../../../../environments/environment';
import { AppTelemetryService } from '@core/observability/app-telemetry.service';

export interface TransferPersonCommunityRequest {
  targetUnitId: string;
  effectiveDate: string;
  reason?: string | null;
}

export interface UpdatePersonStatusRequest {
  status: MembershipStatus;
  effectiveDate?: string | null;
  deathDate?: string | null;
}

export interface PersonDivineServiceAttendance {
  divineServiceId: string;
  startAt: string;
  divineServiceTypeName: string;
  organizationUnitId: string;
  organizationUnitName: string;
  attendanceCategory: 'Member' | 'Visitor';
  ageStatus: string;
  isSealed: boolean;
  isClosed: boolean;
}

export interface PersonRelationshipItem { id?: number; relationshipType: 'Parent' | 'Child'; relatedPersonId?: string | null; relatedPersonName: string; isRelatedPersonDeceased?: boolean; validFrom?: string | null; validTo?: string | null; notes?: string | null; }
export interface PersonFamilyRelationships { parents: PersonRelationshipItem[]; children: PersonRelationshipItem[]; }
export interface ResponsiblePriestCandidate { id: string; fullName: string; ministryName: string; }
export interface ResponsiblePriestMember { id: string; firstName: string; lastName: string; phone?: string; membershipUnitName?: string; }
export interface DeletedPerson { id: string; fullName: string; portalMemberId?: string; documentNumber?: string; membershipUnitName?: string; deletedAtUtc?: string; deletionReason?: string; }
export interface AdminPerson { id: string; firstName: string; lastName: string; portalMemberId?: string; documentNumber?: string; membershipUnitName?: string; status: string; isDeleted: boolean; isMerged: boolean; deletedAtUtc?: string; lastModifiedAtUtc?: string; }
export interface AdminPeoplePage { items: AdminPerson[]; totalCount: number; pageNumber: number; pageSize: number; }

@Injectable({
  providedIn: 'root'
})
export class PersonService {
  constructor(
    private readonly api: PeopleApiService,
    private readonly http: HttpClient,
    private readonly telemetry: AppTelemetryService,
  ) {}

  getPerson(personId: string): Observable<Person> {
    // El contrato generado todavía no incluye la situación de pareja. Usamos el
    // DTO JSON directamente para no perder sus campos al deserializarlo.
    return this.http.get<PersonDto>(`${environment.apiUrl}/people/${personId}`).pipe(
      map(dto => mapPersonDtoToPerson(dto))
    );
  }

  createPerson(data: CreatePersonRequest): Observable<Person> {
    return this.http.post<PersonDto>(`${environment.apiUrl}/people`, data).pipe(
      map(dto => mapPersonDtoToPerson(dto))
    );
  }

  updatePerson(personId: string, data: UpdatePersonRequest): Observable<Person> {
    return this.http.put<PersonDto>(`${environment.apiUrl}/people/${personId}`, data).pipe(
      map(dto => mapPersonDtoToPerson(dto))
    );
  }

  updateStatus(personId: string, request: UpdatePersonStatusRequest): Observable<Person> {
    return this.http.put<PersonDto>(`${environment.apiUrl}/people/${personId}/status`, request).pipe(
      map(dto => mapPersonDtoToPerson(dto))
    );
  }

  deletePerson(personId: string, reason: string): Observable<void> {
    const deleteDto = new DeletePersonDto();
    deleteDto.deletionReason = reason;
    return this.api.delete(personId, deleteDto);
  }

  restorePerson(personId: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/people/${personId}/restore`, {});
  }

  permanentlyDeletePerson(personId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/people/${personId}/permanent`);
  }

  permanentlyDeletePeople(personIds: string[]): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/people/permanent/bulk`, personIds);
  }

  getDeletedPeople(searchTerm?: string): Observable<DeletedPerson[]> {
    const params = searchTerm ? { searchTerm } : undefined;
    return this.http.get<DeletedPerson[]>(`${environment.apiUrl}/people/deleted`, { params });
  }

  getAdminPeople(page: number, pageSize: number, searchTerm: string, isDeleted: string, unitId?: string): Observable<AdminPeoplePage> {
    const params: Record<string, string | number> = { page, pageSize };
    if (searchTerm) params['searchTerm'] = searchTerm;
    if (isDeleted !== 'all') params['isDeleted'] = isDeleted;
    if (unitId) params['unitId'] = unitId;
    return this.http.get<AdminPeoplePage>(`${environment.apiUrl}/people/admin-list`, { params });
  }

  transferCommunity(personId: string, request: TransferPersonCommunityRequest): Observable<void> {
    return this.telemetry.trackOperation(
      this.http.post<void>(`${environment.apiUrl}/people/${personId}/community-transfer`, request),
      {
        successEventName: 'person.community_transferred',
        attributes: {
          personId,
          targetUnitId: request.targetUnitId,
        },
      },
    );
  }

  validateCommunityTransferDate(personId: string, effectiveDate: string): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}/people/${personId}/community-transfer/validate-date`,
      { effectiveDate }
    );
  }

  getCommunityHistory(personId: string): Observable<PersonCommunityHistoryItem[]> {
    return this.http.get<PersonCommunityHistoryItem[]>(`${environment.apiUrl}/people/${personId}/community-history`);
  }

  getDivineServiceAttendance(personId: string, take = 5): Observable<PersonDivineServiceAttendance[]> {
    return this.http.get<PersonDivineServiceAttendance[]>(
      `${environment.apiUrl}/people/${personId}/divine-service-attendance`,
      { params: { take } }
    );
  }

  getFamilyRelationships(personId: string): Observable<PersonFamilyRelationships> {
    return this.http.get<PersonFamilyRelationships>(`${environment.apiUrl}/people/${personId}/family-relationships`);
  }

  updateHomeAttendance(personId: string, isHomeAttended: boolean): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/people/${personId}/home-attendance`, { isHomeAttended });
  }

  getResponsiblePriestCandidates(personId: string): Observable<ResponsiblePriestCandidate[]> {
    return this.http.get<ResponsiblePriestCandidate[]>(`${environment.apiUrl}/people/${personId}/responsible-priest-candidates`);
  }

  getResponsiblePriestCandidatesByUnit(unitId: string): Observable<ResponsiblePriestCandidate[]> {
    return this.http.get<ResponsiblePriestCandidate[]>(`${environment.apiUrl}/people/responsible-priest-candidates`, { params: { unitId } });
  }

  getTrustedMembers(personId: string): Observable<ResponsiblePriestMember[]> {
    return this.http.get<ResponsiblePriestMember[]>(`${environment.apiUrl}/people/${personId}/trusted-members`);
  }

  updateResponsiblePriest(personId: string, responsiblePriestId: string | null): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/people/${personId}/responsible-priest`, { responsiblePriestId });
  }

  saveFamilyRelationships(personId: string, request: PersonFamilyRelationships): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/people/${personId}/family-relationships`, request);
  }

  createCommunityHistory(personId: string, request: CreatePersonCommunityHistoryRequest): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/people/${personId}/community-history`, request);
  }

  updateCommunityHistory(personId: string, historyId: number, request: UpdatePersonCommunityHistoryRequest): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/people/${personId}/community-history/${historyId}`, request);
  }

  deleteCommunityHistory(personId: string, historyId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/people/${personId}/community-history/${historyId}`);
  }

  getAuthorizedUnits(): Observable<OrganizationUnitItem[]> {
    return this.api.getAuthorizedUnits().pipe(
      map(dtos => dtos
        .filter(dto => !!dto.id)
        .map(dto => ({
          id: dto.id!,
          name: dto.name ?? '',
          levelId: dto.organizationLevelId ?? 0,
          levelName: dto.levelName ?? 'Sin nivel',
          parentId: dto.parentUnitId,
          ancestorNames: dto.ancestorNames ?? [],
        }))
      )
    );
  }

  getCommunityTransferTargets(personId: string): Observable<OrganizationUnitItem[]> {
    return this.http.get<OrganizationUnitDto[]>(`${environment.apiUrl}/people/${personId}/community-transfer-targets`).pipe(
      map(dtos => dtos
        .filter(dto => !!dto.id)
        .map(dto => ({
          id: dto.id!,
          name: dto.name ?? '',
          levelId: dto.organizationLevelId ?? 0,
          levelName: dto.levelName ?? 'Sin nivel',
          parentId: dto.parentUnitId,
          ancestorNames: dto.ancestorNames ?? [],
        }))
      )
    );
  }

  createMinisterialActivityEvent(
    personId: string,
    request: {
      eventType: MinistryEventType;
      effectiveDate: string;
      expectedEndDate?: string;
      keepCurrentMinistryAreas?: boolean;
      actOrganizationUnitId?: string;
      actLocation?: string;
      reason?: string;
      membershipCommunityId?: string;
      areas: Array<{
        kind: MinisterialActivityKind;
        referenceId: number;
        organizationUnitId?: string | null;
        historicalOrganizationUnitName?: string | null;
        expectedEndDate?: string;
      }>;
      reassignments?: Array<{
        kind: MinisterialActivityKind;
        referenceId: number;
        fromOrganizationUnitId?: string | null;
        fromHistoricalOrganizationUnitName?: string | null;
        toOrganizationUnitId?: string | null;
        toHistoricalOrganizationUnitName?: string | null;
      }>;
    },
  ): Observable<number> {
    return this.http.post<number>(`${environment.apiUrl}/people/${personId}/ministerial-activity/events`, request);
  }

  createOfficiantCollaboration(
    personId: string,
    request: {
      effectiveDate: string;
      organizationUnitId: string;
      reason?: string;
    },
  ): Observable<number> {
    return this.http.post<number>(
      `${environment.apiUrl}/people/${personId}/ministerial-activity/officiant-collaboration`,
      request,
    );
  }

  updateOfficiantCollaboration(
    personId: string,
    activityId: number,
    request: {
      effectiveDate: string;
      endDate?: string;
      organizationUnitId: string;
      reason?: string;
    },
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.apiUrl}/people/${personId}/ministerial-activity/officiant-collaboration/${activityId}`,
      request,
    );
  }

  deleteOfficiantCollaboration(personId: string, activityId: number): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}/people/${personId}/ministerial-activity/officiant-collaboration/${activityId}`,
    );
  }

  updateMinisterialActivityEvent(
    personId: string,
    eventId: number,
    request: {
      eventType: MinistryEventType;
      effectiveDate: string;
      expectedEndDate?: string;
      keepCurrentMinistryAreas?: boolean;
      actOrganizationUnitId?: string;
      actLocation?: string;
      reason?: string;
      areas: Array<{
        kind: MinisterialActivityKind;
        referenceId: number;
        organizationUnitId?: string | null;
        historicalOrganizationUnitName?: string | null;
        expectedEndDate?: string;
      }>;
      reassignments?: Array<{
        kind: MinisterialActivityKind;
        referenceId: number;
        fromOrganizationUnitId?: string | null;
        fromHistoricalOrganizationUnitName?: string | null;
        toOrganizationUnitId?: string | null;
        toHistoricalOrganizationUnitName?: string | null;
      }>;
    },
  ): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/people/${personId}/ministerial-activity/events/${eventId}`, request);
  }

  getMinisterialActivityEffectiveState(
    personId: string,
    effectiveDate: string,
    excludeEventId?: number,
  ): Observable<MinisterialActivityEffectiveState> {
    const params: Record<string, string | number> = { effectiveDate };
    if (excludeEventId !== undefined) {
      params['excludeEventId'] = excludeEventId;
    }
    return this.http.get<MinisterialActivityEffectiveState>(
      `${environment.apiUrl}/people/${personId}/ministerial-activity/effective-state`,
      { params },
    ).pipe(map(state => ({
      ...state,
      designations: Array.isArray(state?.designations) ? state.designations : [],
    })));
  }

  getMinisterialActivityActions(): Observable<MinisterialActivityActionDefinition[]> {
    return this.http.get<MinisterialActivityActionDefinition[]>(
      `${environment.apiUrl}/people/ministerial-activity/actions`,
    ).pipe(map(definitions => (Array.isArray(definitions) ? definitions : [])
      .map(definition => this.mapMinisterialActivityActionDefinition(definition))
      .filter((definition): definition is MinisterialActivityActionDefinition => !!definition)));
  }

  private mapMinisterialActivityActionDefinition(
    definition: MinisterialActivityActionDefinition | (Partial<MinisterialActivityActionDefinition> & Record<string, unknown>),
  ): MinisterialActivityActionDefinition | null {
    const rawDefinition = definition as Partial<MinisterialActivityActionDefinition> & Record<string, unknown>;
    const read = <T>(camelCaseName: keyof MinisterialActivityActionDefinition, pascalCaseName: string, fallback: T): T =>
      (rawDefinition[camelCaseName] ?? rawDefinition[pascalCaseName] ?? fallback) as T;
    const eventType = read<MinistryEventType | null>('eventType', 'EventType', null);
    if (!eventType) {
      return null;
    }

    return {
      eventType,
      requiresEffectiveDate: read<boolean>('requiresEffectiveDate', 'RequiresEffectiveDate', true),
      requiresActPlace: read<boolean>('requiresActPlace', 'RequiresActPlace', false),
      requiresComment: read<boolean>('requiresComment', 'RequiresComment', false),
      requiresMinistry: read<boolean>('requiresMinistry', 'RequiresMinistry', false),
      requiresAppointmentOrAssignment: read<boolean>('requiresAppointmentOrAssignment', 'RequiresAppointmentOrAssignment', false),
      requiresArea: read<boolean>('requiresArea', 'RequiresArea', false),
      requiresExpectedEndDate: read<boolean>('requiresExpectedEndDate', 'RequiresExpectedEndDate', false),
      targetStatus: read<MinistryStatus | undefined>('targetStatus', 'TargetStatus', undefined),
    };
  }

  previewMinisterialActivityTransition(
    personId: string,
    request: {
      eventType: MinistryEventType;
      effectiveDate: string;
      expectedEndDate?: string;
      keepCurrentMinistryAreas?: boolean;
      actOrganizationUnitId?: string;
      actLocation?: string;
      reason?: string;
      areas: Array<{
        kind: MinisterialActivityKind;
        referenceId: number;
        organizationUnitId?: string | null;
        historicalOrganizationUnitName?: string | null;
        expectedEndDate?: string;
      }>;
      reassignments?: Array<{
        kind: MinisterialActivityKind;
        referenceId: number;
        fromOrganizationUnitId?: string | null;
        fromHistoricalOrganizationUnitName?: string | null;
        toOrganizationUnitId?: string | null;
        toHistoricalOrganizationUnitName?: string | null;
      }>;
    },
    excludeEventId?: number,
  ): Observable<MinisterialActivityTransitionPreview> {
    const params = excludeEventId === undefined ? undefined : { excludeEventId };
    return this.http.post<MinisterialActivityTransitionPreview>(
      `${environment.apiUrl}/people/${personId}/ministerial-activity/preview`,
      request,
      { params },
    );
  }

  createMinistryActivity(personId: string, request: { unitId: string; validFrom: string }): Observable<number> {
    return this.http.post<number>(`${environment.apiUrl}/people/${personId}/ministry-activities`, request);
  }

  endMinistryActivity(personId: string, activityId: number, request: { validTo: string }): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/people/${personId}/ministry-activities/${activityId}/end`, request);
  }

  createAppointment(
    personId: string,
    request: { appointmentId: number; unitId?: string; actOrganizationUnitId?: string; actLocation?: string; validFrom: string; validTo?: string; membershipCommunityId?: string },
  ): Observable<number> {
    return this.http.post<number>(`${environment.apiUrl}/people/${personId}/appointments`, request);
  }

  updateAppointment(
    personId: string,
    appointmentEventId: number,
    request: { appointmentId: number; unitId?: string; actOrganizationUnitId?: string; actLocation?: string; validFrom: string; validTo?: string },
  ): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/people/${personId}/appointments/${appointmentEventId}`, request);
  }

  deleteAppointment(personId: string, appointmentEventId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/people/${personId}/appointments/${appointmentEventId}`);
  }

  createAssignment(
    personId: string,
    request: { assignmentId: number; unitId?: string; actOrganizationUnitId?: string; actLocation?: string; validFrom: string; validTo?: string; membershipCommunityId?: string },
  ): Observable<number> {
    return this.http.post<number>(`${environment.apiUrl}/people/${personId}/assignments`, request);
  }

  updateAssignment(
    personId: string,
    assignmentEventId: number,
    request: { assignmentId: number; unitId?: string; actOrganizationUnitId?: string; actLocation?: string; validFrom: string; validTo?: string },
  ): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/people/${personId}/assignments/${assignmentEventId}`, request);
  }

  deleteAssignment(personId: string, assignmentEventId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/people/${personId}/assignments/${assignmentEventId}`);
  }

  searchPeople(searchTerm: string, unitIds?: string[]): Observable<PersonLookupOption[]> {
    return this.api.get(searchTerm, unitIds ?? null, 'firstName', 'asc', 1, 10).pipe(
      map(response => (response.items ?? [])
        .filter(dto => !!dto.id)
        .map(dto => ({
          id: dto.id!,
          name: `${dto.firstName ?? ''} ${dto.lastName ?? ''}`.trim(),
          subtitle: dto.membershipUnitName ?? undefined,
        })))
    );
  }
}
